import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// --- Constants & Data ---

// === 2026 price increase ===
// Blanket multiplier applied ONLY to the door's made-to-order, size-based price
// (base + oversize dimensional fees). It does NOT touch the add-on options
// (assembled service, premium frame, mesh upgrades). Set to 1.0 to revert.
// NOTE: this changes only the on-screen ESTIMATE. The real charged price is
// computed by the ACO app and must be raised there too, or the cart total
// shown to the customer at checkout won't match this estimate.
const PRICE_INCREASE = 1.10;

const PRICES = {
  base: 160 * PRICE_INCREASE,
  assembly: {
    unassembled: 0,
    assembled: 70,
  },
  frameType: {
    classic: 0,
    premium: 55,
  },
  mesh: {
    standard: 0,
    solar: 28,
    bettervue: 28,
    ultravue: 30,
    petscreen: 32,
  },
  // Dimensional surcharges that match the live ACO rules:
  oversizeWidthMin: 38,
  oversizeWidthFee: 27.5 * PRICE_INCREASE,
  oversizeHeightMin: 82,
  oversizeHeightFee: 30 * PRICE_INCREASE,
};

// Screen doors are not part of the volume-discount program (window screens
// only). Keep the qty stepper but show a simple `qty × unit` total.
const MAX_QTY = 99;

// Frame colors mirror the window-screen configurator's swatches (src/screen.jsx)
// so Tan-on-a-door visually matches Tan-on-a-window. `mill` is door-only and
// has no exact window-screen equivalent, so its hex is left unchanged.
const COLORS = {
  white: { name: 'White', hex: '#FFFFFF', border: '#cbd5e1' },
  tan: { name: 'Tan', hex: '#E8DCCC', border: '#c4b49e' },
  bronze: { name: 'Bronze', hex: '#3E3330', border: '#2a2220' },
  champagne: { name: 'Champagne', hex: '#BDB8B2', border: '#9a9590' },
  mill: { name: 'Mill / Silver', hex: '#C0C0C0', border: '#999999' },
  black: { name: 'Black', hex: '#1a1a1a', border: '#000000' },
};

const MESH_STYLES = {
  standard: { name: 'Charcoal Fiberglass', opacity: 0.3, color: '#222', pattern: 'grid' },
  solar: { name: 'Solar Insect', opacity: 0.7, color: '#111', pattern: 'dense' },
  bettervue: { name: 'BetterVue', opacity: 0.2, color: '#222', pattern: 'fine' },
  ultravue: { name: 'UltraVue', opacity: 0.15, color: '#222', pattern: 'fine' },
  petscreen: { name: 'PetScreen', opacity: 0.6, color: '#000', pattern: 'thick' },
};

const FRACTIONS = [
  { val: 0, label: '0"' },
  { val: 0.0625, label: '1/16"' },
  { val: 0.125, label: '1/8"' },
  { val: 0.1875, label: '3/16"' },
  { val: 0.25, label: '1/4"' },
  { val: 0.3125, label: '5/16"' },
  { val: 0.375, label: '3/8"' },
  { val: 0.4375, label: '7/16"' },
  { val: 0.5, label: '1/2"' },
  { val: 0.5625, label: '9/16"' },
  { val: 0.625, label: '5/8"' },
  { val: 0.6875, label: '11/16"' },
  { val: 0.75, label: '3/4"' },
  { val: 0.8125, label: '13/16"' },
  { val: 0.875, label: '7/8"' },
  { val: 0.9375, label: '15/16"' },
];

const WIDTH_RANGE = { min: 22, max: 60 };
const HEIGHT_RANGE = { min: 67, max: 95 };

// Reference images from the Shopify product gallery — used as small
// click-to-zoom thumbnails below the live preview so customers can see the
// real profile/spec diagrams instead of just the SVG rendering.
const REFERENCE_IMAGES = {
  classic: [
    {
      url: 'https://cdn.shopify.com/s/files/1/0070/3738/6819/files/Classic_Profile_Diagram.png',
      label: 'Classic frame profile',
    },
    {
      url: 'https://cdn.shopify.com/s/files/1/0070/3738/6819/files/Classic_Combined.png',
      label: 'Classic frame & roller',
    },
  ],
  premium: [
    {
      url: 'https://cdn.shopify.com/s/files/1/0070/3738/6819/files/Premium_Profile_Diagram_v3.png',
      label: 'Premium frame profile',
    },
    {
      url: 'https://cdn.shopify.com/s/files/1/0070/3738/6819/files/Premium_Combined.png',
      label: 'Premium frame & roller',
    },
  ],
};
const MEASUREMENT_GUIDE_IMG = {
  url: 'https://cdn.shopify.com/s/files/1/0070/3738/6819/files/Screen-Door-measurement-instructions-_1.jpg',
  label: 'How to measure',
};

// --- Save my Build helpers ---
// State is encoded into a URL-safe base64 string so the build can be:
//   1. Persisted on the Shopify cart (cart.attributes['Saved Build URL'])
//   2. Mirrored to localStorage as a same-browser fallback
//   3. Shared / bookmarked by the customer
const SAVED_BUILD_STORAGE_KEY = 'ssc_door_saved_build';
const SAVED_BUILD_CART_ATTR = 'Saved Door Build';
const SAVED_BUILD_URL_ATTR = 'Saved Door Build URL';

function encodeBuild(state) {
  try {
    const json = JSON.stringify(state);
    return btoa(unescape(encodeURIComponent(json)))
      .replace(/=+$/, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  } catch (e) {
    return '';
  }
}

function decodeBuild(encoded) {
  if (!encoded || typeof encoded !== 'string') return null;
  try {
    let s = encoded.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return JSON.parse(decodeURIComponent(escape(atob(s))));
  } catch (e) {
    return null;
  }
}

function buildSummary(state) {
  // Human-readable summary shown on the cart page next to the saved build link
  const w = (state.width + (state.widthFraction || 0));
  const h = (state.height + (state.heightFraction || 0));
  const colorName = state.color ? state.color.charAt(0).toUpperCase() + state.color.slice(1) : '';
  const frameName = state.frameType === 'premium' ? 'Premium' : 'Classic';
  const assemblyName = state.assembly === 'assembled' ? 'Assembled' : 'DIY Kit';
  return `${w}" × ${h}" · ${colorName} ${frameName} · ${assemblyName}`;
}

// WCPA Field Mappings - Map configurator values to WCPA select element IDs and values
const WCPA_MAPPINGS = {
  assembly: {
    selectId: 'field_select_1086476574',
    values: {
      unassembled: 'Unassembled - DIY',
      assembled: 'Assembled',
    }
  },
  frameType: {
    selectId: 'field_select_4235397328',
    values: {
      classic: 'Classic Door Frame',
      premium: 'Premium Door Frame',
    }
  },
  color: {
    selectId: 'field_select_6406538797',
    values: {
      white: 'White',
      tan: 'Tan',
      bronze: 'Bronze',
      champagne: 'Champagne',
      mill: 'Mill/Silver (Classic Profile Only)',
      black: 'Black (Classic Profile Only)',
    }
  },
  mesh: {
    selectId: 'field_select_6925369348',
    values: {
      standard: 'Charcoal Fiberglass: Free',
      solar: 'Solar Insect',
      bettervue: 'BetterVue',
      ultravue: 'UltraVue',
      petscreen: 'PetScreen',
    }
  },
  width: {
    selectId: 'field_select_8350459804',
  },
  widthFraction: {
    selectId: 'field_select-1933511665',
  },
  height: {
    selectId: 'field_select_4104811047',
  },
  heightFraction: {
    selectId: 'field_select_3886010414',
  },
  handleLocation: {
    // Real ID from the live ACO config (Handle Location — Premium Door Only)
    selectId: 'field_select_6186312153',
    values: {
      left: 'Left (From Outside Looking In)',
      right: 'Right (From Outside Looking In)',
      none: 'No handle',
    }
  }
};

// --- Helper Components ---

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="10 8 16 12 10 16 10 8"></polygon>
  </svg>
);

const BookmarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
  </svg>
);

const CartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"></path>
  </svg>
);

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TruckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13"></rect>
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
    <circle cx="5.5" cy="18.5" r="2.5"></circle>
    <circle cx="18.5" cy="18.5" r="2.5"></circle>
  </svg>
);

const RadioCard = ({ selected, onClick, title, price, subtitle, children }) => (
  <div
    onClick={onClick}
    className={`ssc-radio-card ${selected ? 'ssc-radio-card--selected' : ''}`}
  >
    <div>
      <div className="ssc-radio-card__header">
        <div>
          <div className={`ssc-radio-card__title ${selected ? 'ssc-radio-card__title--selected' : ''}`}>{title}</div>
          {subtitle && <div className="ssc-radio-card__subtitle">{subtitle}</div>}
        </div>
        {selected && <div className="ssc-radio-card__check"><CheckIcon /></div>}
      </div>
      {price !== undefined && (
        <div className={`ssc-radio-card__price ${price > 0 ? '' : 'ssc-radio-card__price--included'}`}>
          {price === 0 ? 'Included' : price > 0 ? `+$${price.toFixed(2)}` : `-$${Math.abs(price).toFixed(2)}`}
        </div>
      )}
    </div>
    {children && <div className="ssc-radio-card__content">{children}</div>}
  </div>
);

// --- Main Component ---

export default function ShopifyConfigurator({
  onConfigChange,
  showPreview = true,
  showPriceSummary = false,
  // Whether the Shopify ACO field for the Assembled variant currently exists.
  // The live ACO field only has "Unassembled - DIY" right now, so we hide
  // the Assembled card to avoid writing a value ACO doesn't recognize.
  // Flip to true (or pass `window.SSC_CONFIG.assembledAvailable = true`) once
  // you add the Assembled option back to ACO.
  assembledAvailable = (typeof window !== 'undefined' && window.SSC_CONFIG && window.SSC_CONFIG.assembledAvailable) || false,
}) {
  // State
  const [assembly, setAssembly] = useState('unassembled');
  const [frameType, setFrameType] = useState('classic');
  const [handleLocation, setHandleLocation] = useState('right');
  const [color, setColor] = useState('white');
  const [mesh, setMesh] = useState('standard');
  const [width, setWidth] = useState(36);
  const [widthFraction, setWidthFraction] = useState(0);
  const [height, setHeight] = useState(80);
  const [heightFraction, setHeightFraction] = useState(0);
  const [quantity, setQuantity] = useState(1);

  // Save my Build — toast + restored notice
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error' | null
  const [restoredNotice, setRestoredNotice] = useState(false);
  // Add to Cart — visual feedback while we sync + submit
  const [cartStatus, setCartStatus] = useState(null); // 'adding' | 'added' | null

  // Click-to-zoom lightbox for reference image thumbnails
  const [lightboxImg, setLightboxImg] = useState(null);

  // Refs for the JS-based scroll-follow on the Live Preview (desktop only).
  // CSS position:sticky doesn't survive on Shopify because the theme has
  // overflow:hidden ancestors that break the sticky context.
  const previewColumnRef = useRef(null);
  const previewInnerRef = useRef(null);
  const atcCardRef = useRef(null);

  // Computed Values
  const totalWidth = width + widthFraction;
  const totalHeight = height + heightFraction;

  // Handle Logic: Reset incompatible colors
  useEffect(() => {
    if (frameType === 'premium' && (color === 'mill' || color === 'black' || color === 'white')) {
      setColor('tan');
    }
  }, [frameType, color]);

  // If the live ACO doesn't have the Assembled variant configured, force
  // assembly back to "unassembled" so we never write a value ACO can't match.
  // This also covers the case where a saved-build URL had Assembled selected
  // back when it was available.
  useEffect(() => {
    if (!assembledAvailable && assembly === 'assembled') {
      setAssembly('unassembled');
    }
  }, [assembledAvailable, assembly]);

  // Hydrate state from a saved build, if one exists.
  // Priority: URL query string  >  Shopify cart attribute  >  localStorage.
  const hydrateFromSavedBuild = useCallback((decoded, source) => {
    if (!decoded || typeof decoded !== 'object') return false;
    if (decoded.assembly) setAssembly(decoded.assembly);
    if (decoded.frameType) setFrameType(decoded.frameType);
    if (decoded.handleLocation) setHandleLocation(decoded.handleLocation);
    if (decoded.color) setColor(decoded.color);
    if (decoded.mesh) setMesh(decoded.mesh);
    if (typeof decoded.width === 'number') setWidth(decoded.width);
    if (typeof decoded.widthFraction === 'number') setWidthFraction(decoded.widthFraction);
    if (typeof decoded.height === 'number') setHeight(decoded.height);
    if (typeof decoded.heightFraction === 'number') setHeightFraction(decoded.heightFraction);
    // Restore quantity only if the URL didn't already set it (URL `?qty=`
    // from the cart is authoritative; this is the fallback for Save my Build
    // hydration where no separate qty param exists).
    if (typeof decoded.quantity === 'number' && decoded.quantity >= 1) {
      setQuantity((prev) => prev === 1 ? Math.min(99, decoded.quantity) : prev);
    }
    setRestoredNotice(true);
    setTimeout(() => setRestoredNotice(false), 6000);
    console.log(`[SSC] Restored saved build from ${source}`);
    return true;
  }, []);

  useEffect(() => {
    let cancelled = false;

    // 1. URL ?build=... (and optional ?edit_line= and ?qty= for cart-edit flows)
    try {
      const params = new URLSearchParams(window.location.search);
      const editKey = params.get('edit_line');
      if (editKey) setEditLineKey(editKey);
      // Restore quantity from the URL (set by the cart's Edit link based on
      // the cart line's current quantity).
      const qtyParam = parseInt(params.get('qty'), 10);
      if (!Number.isNaN(qtyParam) && qtyParam >= 1) {
        setQuantity(Math.min(99, qtyParam));
      }
      const fromUrl = params.get('build');
      if (fromUrl) {
        const decoded = decodeBuild(fromUrl);
        if (decoded && hydrateFromSavedBuild(decoded, editKey ? 'cart-edit URL' : 'URL')) return;
      }
    } catch (e) { /* ignore */ }

    // 2. Shopify cart attribute
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(cart => {
        if (cancelled || !cart) return false;
        const url = cart.attributes && cart.attributes[SAVED_BUILD_URL_ATTR];
        if (url) {
          try {
            const u = new URL(url, window.location.origin);
            const fromCart = u.searchParams.get('build');
            const decoded = decodeBuild(fromCart);
            if (decoded && hydrateFromSavedBuild(decoded, 'cart')) return true;
          } catch (e) { /* ignore */ }
        }
        return false;
      })
      .then(handled => {
        if (cancelled || handled) return;
        // 3. localStorage fallback (same browser only)
        try {
          const local = localStorage.getItem(SAVED_BUILD_STORAGE_KEY);
          if (local) {
            const decoded = decodeBuild(local);
            if (decoded) hydrateFromSavedBuild(decoded, 'localStorage');
          }
        } catch (e) { /* ignore */ }
      })
      .catch(() => { /* not on a Shopify page — that's fine */ });

    return () => { cancelled = true; };
  }, [hydrateFromSavedBuild]);

  // Save my Build action — writes the configuration to (a) the Shopify cart
  // as a cart attribute and (b) localStorage as a same-browser fallback.
  const handleSaveMyBuild = useCallback(async () => {
    const state = {
      assembly, frameType, handleLocation, color, mesh,
      width, widthFraction, height, heightFraction,
    };
    const encoded = encodeBuild(state);
    if (!encoded) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
      return;
    }

    setSaveStatus('saving');

    // Build the deep-link URL the customer can return to
    const restoreUrl = new URL(window.location.href);
    restoreUrl.searchParams.set('build', encoded);
    const restoreUrlString = restoreUrl.toString();

    // localStorage (immediate, never fails offline)
    try {
      localStorage.setItem(SAVED_BUILD_STORAGE_KEY, encoded);
    } catch (e) { /* private mode / quota */ }

    // Shopify cart attribute (cross-session persistence)
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          attributes: {
            [SAVED_BUILD_CART_ATTR]: buildSummary(state),
            [SAVED_BUILD_URL_ATTR]: restoreUrlString,
          },
        }),
      });
      if (!res.ok) throw new Error(`/cart/update.js returned ${res.status}`);
    } catch (e) {
      console.warn('[SSC] Could not save to cart attributes (likely not on Shopify):', e.message);
      // localStorage already saved; that's our fallback. Still consider it a success.
    }

    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(null), 4000);
  }, [assembly, frameType, handleLocation, color, mesh, width, widthFraction, height, heightFraction]);

  // Track whether this session is editing an existing cart line.
  // Populated from `?edit_line=KEY` on mount; if set, ATC will remove that
  // line after a successful add so the customer ends up with the edited
  // version only.
  const [editLineKey, setEditLineKey] = useState(null);

  // Add to Cart — sync the configurator's quantity to the Shopify product
  // form's hidden quantity input, inject the encoded build as a hidden line
  // item property, then click the (visually hidden) native submit button.
  // If we're editing an existing line, remove that line FIRST so it survives
  // any redirect to /cart the theme might do after the new add.
  const handleAddToCart = useCallback(async () => {
    setCartStatus('adding');

    const form =
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('.product-form');

    // 0. EDIT FLOW: remove the original line before submitting the new add.
    //    Most Shopify themes redirect to /cart after the form submit, which
    //    kills any deferred setTimeout. Pre-emptive removal guarantees the
    //    old line is gone regardless of theme behavior.
    if (editLineKey) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: editLineKey, quantity: 0 }),
        });
        console.log('[SSC] Removed original cart line', editLineKey);
      } catch (e) {
        console.warn('[SSC] Could not remove original line:', e);
      }
    }

    // 1. Sync quantity
    const qtyInput =
      (form && form.querySelector('input[name="quantity"]')) ||
      document.querySelector('input[name="quantity"]');
    if (qtyInput) {
      const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      setter.call(qtyInput, String(quantity));
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // 2. Inject `properties[_build]` so the cart can offer an Edit button
    //    later. Underscore prefix hides it from the cart's visible property list.
    if (form) {
      const state = {
        product: 'door',
        assembly, frameType, handleLocation, color, mesh,
        width, widthFraction, height, heightFraction,
        quantity,
      };
      const encoded = encodeBuild(state);
      let buildInput = form.querySelector('input[name="properties[_build]"]');
      if (!buildInput) {
        buildInput = document.createElement('input');
        buildInput.type = 'hidden';
        buildInput.name = 'properties[_build]';
        form.appendChild(buildInput);
      }
      buildInput.value = encoded;
    }

    // 3. Click the real submit button
    const realBtn =
      (form && form.querySelector('[type="submit"]')) ||
      document.querySelector('.product-form__submit') ||
      document.querySelector('button[name="add"]') ||
      document.querySelector('[data-add-to-cart]');

    if (!realBtn) {
      console.warn('[SSC] Add to Cart: no Shopify form submit button found.');
      setCartStatus(null);
      return;
    }
    realBtn.click();
    // (Removal of the original line is done above before the submit, so it
    //  survives the theme's potential redirect.)

    setTimeout(() => {
      setCartStatus('added');
      setTimeout(() => setCartStatus(null), 2200);
    }, 300);
  }, [quantity, assembly, frameType, handleLocation, color, mesh, width, widthFraction, height, heightFraction, editLineKey]);

  // --- JS-based scroll-follow for the Live Preview ---
  // Mirrors the window-screen configurator's approach. Manually positions
  // the preview card as `fixed` while scrolling, and parks it just above
  // the Add-to-Cart card when that comes into view. Desktop only.
  useEffect(() => {
    if (!showPreview) return;
    const col = previewColumnRef.current;
    const inner = previewInnerRef.current;
    const atc = atcCardRef.current;
    if (!col || !inner || !atc) return;

    const mql = window.matchMedia('(min-width: 1024px)');
    let ticking = false;
    const topOffset = 16;

    const update = () => {
      ticking = false;
      if (!mql.matches) {
        // Mobile: reset everything, let it flow naturally
        inner.style.position = '';
        inner.style.top = '';
        inner.style.width = '';
        return;
      }

      const colRect = col.getBoundingClientRect();
      const innerH = inner.offsetHeight;
      const atcRect = atc.getBoundingClientRect();
      const bottomLimit = atcRect.top - 16;

      if (colRect.top >= topOffset) {
        // Column top still in view — natural position
        inner.style.position = '';
        inner.style.top = '';
        inner.style.width = '';
      } else if (topOffset + innerH <= bottomLimit) {
        // Fix in viewport while there's still room above the ATC card
        inner.style.position = 'fixed';
        inner.style.top = topOffset + 'px';
        inner.style.width = colRect.width + 'px';
      } else {
        // ATC is close — pin the preview just above it within the column
        const parentRect = col.getBoundingClientRect();
        const pinnedTop = atcRect.top - innerH - 16 - parentRect.top;
        inner.style.position = 'relative';
        inner.style.top = Math.max(0, pinnedTop) + 'px';
        inner.style.width = '';
      }
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    const onResize = () => update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      inner.style.position = '';
      inner.style.top = '';
      inner.style.width = '';
    };
  }, [showPreview]);

  // Clear a saved build (used when the customer adds to cart for real, or
  // explicitly dismisses the "restored" notice).
  const handleClearSavedBuild = useCallback(async () => {
    try { localStorage.removeItem(SAVED_BUILD_STORAGE_KEY); } catch (e) {}
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          attributes: { [SAVED_BUILD_CART_ATTR]: '', [SAVED_BUILD_URL_ATTR]: '' },
        }),
      });
    } catch (e) { /* not on Shopify */ }
    setRestoredNotice(false);
  }, []);

  // Per-unit price (mirrors the live ACO calculation including dimensional
  // surcharges so the cart total preview is accurate).
  const totalPrice = useMemo(() => {
    let total = PRICES.base;
    total += PRICES.assembly[assembly];
    total += PRICES.frameType[frameType];
    total += PRICES.mesh[mesh];
    if (width >= PRICES.oversizeWidthMin) total += PRICES.oversizeWidthFee;
    if (height >= PRICES.oversizeHeightMin) total += PRICES.oversizeHeightFee;
    return total;
  }, [assembly, frameType, mesh, width, height]);

  // Doors don't get volume discounts — just compute the straight cart total.
  const cartSubtotal = useMemo(() => totalPrice * quantity, [totalPrice, quantity]);

  // Format fraction for WCPA dropdown value (no quotes, just the fraction)
  const formatFractionForWCPA = useCallback((fraction) => {
    if (fraction === 0) return '0';
    const fractionObj = FRACTIONS.find(f => f.val === fraction);
    if (!fractionObj) return '0';
    // Return just the fraction part without quotes, e.g., "1/8"
    return fractionObj.label.replace('"', '');
  }, []);

  // Check if ACO fields are ready
  const [acoReady, setAcoReady] = useState(false);
  
  useEffect(() => {
    // Poll for ACO fields to be ready
    const checkAcoReady = () => {
      const firstField = document.getElementById(WCPA_MAPPINGS.assembly.selectId);
      if (firstField) {
        console.log('[SSC] ACO fields detected, ready to sync');
        setAcoReady(true);
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (checkAcoReady()) return;
    
    // Poll every 500ms for up to 10 seconds
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts++;
      if (checkAcoReady() || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts) {
          console.log('[SSC] ACO fields not found after 10 seconds');
        }
      }
    }, 500);
    
    return () => clearInterval(interval);
  }, []);

  // Update WCPA fields whenever config changes AND ACO is ready
  useEffect(() => {
    if (!acoReady) return;
    
    const config = {
      assembly: WCPA_MAPPINGS.assembly.values[assembly],
      frameType: WCPA_MAPPINGS.frameType.values[frameType],
      handleLocation: frameType === 'premium' ? WCPA_MAPPINGS.handleLocation.values[handleLocation] : null,
      color: WCPA_MAPPINGS.color.values[color],
      mesh: WCPA_MAPPINGS.mesh.values[mesh],
      width: `${width}"`,
      widthFraction: formatFractionForWCPA(widthFraction),
      height: `${height}"`,
      heightFraction: formatFractionForWCPA(heightFraction),
      totalWidth,
      totalHeight,
      totalPrice,
    };

    // Update WCPA select elements
    updateWCPAFields(config);

    // Call external callback if provided
    if (onConfigChange) {
      onConfigChange(config);
    }
  }, [acoReady, assembly, frameType, handleLocation, color, mesh, width, widthFraction, height, heightFraction, totalWidth, totalHeight, totalPrice, formatFractionForWCPA, onConfigChange]);

  // Update WCPA/ACO select elements by ID
  const updateWCPAFields = (config) => {
    // Save scroll position before any focus operations
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    
    // Helper to update a select AND its associated hidden field
    const setSelectValue = (selectId, value) => {
      const select = document.getElementById(selectId);
      if (!select) {
        console.log(`[SSC] Select not found: ${selectId}`);
        return false;
      }

      // Find the option that matches our value
      const options = Array.from(select.options);
      const matchingOption = options.find(opt => opt.value === value);
      
      if (matchingOption) {
        // Use focus with preventScroll to avoid page jumping
        select.focus({ preventScroll: true });
        select.value = matchingOption.value;
        
        // Also update the hidden field if it exists
        const numericId = selectId.replace('field_select_', '').replace('field_select', '');
        const hiddenId = `select_${numericId}_hidden`;
        const hiddenInput = document.getElementById(hiddenId);
        if (hiddenInput) {
          hiddenInput.value = matchingOption.value;
          console.log(`[SSC] Updated hidden field ${hiddenId} to: ${matchingOption.value}`);
        }
        
        // Dispatch change event after a microtask to ensure value is set
        setTimeout(() => {
          select.dispatchEvent(new Event('change', { bubbles: true }));
          select.blur();
          // Restore scroll position
          window.scrollTo(scrollX, scrollY);
        }, 0);
        
        console.log(`[SSC] Set ${selectId} to: ${value}`);
        return true;
      } else {
        console.log(`[SSC] No matching option for ${selectId}: ${value}`, options.map(o => o.value));
        return false;
      }
    };

    // Update all WCPA fields
    setSelectValue(WCPA_MAPPINGS.assembly.selectId, config.assembly);
    setSelectValue(WCPA_MAPPINGS.frameType.selectId, config.frameType);
    if (config.handleLocation) {
      setSelectValue(WCPA_MAPPINGS.handleLocation.selectId, config.handleLocation);
    }
    setSelectValue(WCPA_MAPPINGS.color.selectId, config.color);
    setSelectValue(WCPA_MAPPINGS.mesh.selectId, config.mesh);
    setSelectValue(WCPA_MAPPINGS.width.selectId, config.width);
    setSelectValue(WCPA_MAPPINGS.widthFraction.selectId, config.widthFraction);
    setSelectValue(WCPA_MAPPINGS.height.selectId, config.height);
    setSelectValue(WCPA_MAPPINGS.heightFraction.selectId, config.heightFraction);

    // After updating all fields, trigger ACO recalculation
    setTimeout(() => {
      // Restore scroll position after all fields updated
      window.scrollTo(scrollX, scrollY);
      
      if (window.jQuery) {
        const $ = window.jQuery;
        
        // Trigger change events on wcpa container (ACO uses wcpa classes)
        $('.wcpa_form_outer, .wcpa-form-fields').trigger('change');
        
        // Dispatch a custom event that ACO frontend.js might listen to
        document.dispatchEvent(new CustomEvent('wcpa:field:change', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('wcpa:update', { bubbles: true }));
        
        console.log('[SSC] Triggered ACO recalculation events');
      }
      
      // Final scroll restore
      window.scrollTo(scrollX, scrollY);
    }, 150);

    // Dispatch custom event for any external listeners
    window.dispatchEvent(new CustomEvent('ssc:configUpdate', { 
      detail: config,
      bubbles: true 
    }));
  };

  // Available Colors based on Frame Type
  const availableColors = Object.keys(COLORS).filter(key => {
    if (frameType === 'premium') {
      // White is not offered in Premium (Premium + White is unavailable).
      return key !== 'mill' && key !== 'black' && key !== 'white';
    }
    return true;
  });

  // --- Visual Preview Component ---
  const DoorPreview = () => {
    const aspectRatio = totalWidth / totalHeight;
    const svgHeight = 300;
    const svgWidth = svgHeight * aspectRatio;
    const frameThickness = frameType === 'classic' ? 8 : 12;
    const handleStyle = frameType === 'classic' ? 'surface' : 'flush';
    
    const meshStyle = MESH_STYLES[mesh];
    const frameColor = COLORS[color];

    return (
      <div className="ssc-preview">
        <div className="ssc-preview__container">
          <svg 
            width={svgWidth} 
            height={svgHeight} 
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            style={{ overflow: 'visible' }}
          >
            <defs>
              <pattern id="ssc-meshGrid" width="4" height="4" patternUnits="userSpaceOnUse">
                <path d="M 4 0 L 0 0 0 4" fill="none" stroke={meshStyle.color} strokeWidth="0.5" opacity={0.5}/>
              </pattern>
              <pattern id="ssc-meshDense" width="2" height="2" patternUnits="userSpaceOnUse">
                 <path d="M 2 0 L 0 0 0 2" fill="none" stroke={meshStyle.color} strokeWidth="0.8" opacity={0.8}/>
              </pattern>
            </defs>

            {/* Dimension lines */}
            <g transform={`translate(0, -15)`}>
               <line x1="0" y1="0" x2={svgWidth} y2="0" stroke="#64748b" strokeWidth="1" />
               <text x={svgWidth/2} y="-5" textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="bold">
                 {totalWidth.toFixed(4).replace(/\.?0+$/, '')}"
               </text>
            </g>

            <g transform={`translate(-15, 0)`}>
               <line x1="0" y1="0" x2="0" y2={svgHeight} stroke="#64748b" strokeWidth="1" />
               <text x="-5" y={svgHeight/2} textAnchor="middle" transform={`rotate(-90, -5, ${svgHeight/2})`} fill="#64748b" fontSize="11" fontWeight="bold">
                 {totalHeight.toFixed(4).replace(/\.?0+$/, '')}"
               </text>
            </g>

            {/* Door Frame */}
            <g>
              <path 
                d={`M 0 0 H ${svgWidth} V ${svgHeight} H 0 Z M ${frameThickness} ${frameThickness} V ${svgHeight - frameThickness} H ${svgWidth - frameThickness} V ${frameThickness} Z`}
                fill={frameColor.hex} 
                stroke={frameColor.border}
                strokeWidth="1"
                fillRule="evenodd"
              />
              
              <rect 
                x={frameThickness} 
                y={frameThickness} 
                width={svgWidth - (frameThickness * 2)} 
                height={svgHeight - (frameThickness * 2)} 
                fill="#e2e8f0"
              />
              
              <rect 
                x={frameThickness} 
                y={frameThickness} 
                width={svgWidth - (frameThickness * 2)} 
                height={svgHeight - (frameThickness * 2)} 
                fill={meshStyle.pattern === 'dense' || meshStyle.pattern === 'thick' ? 'url(#ssc-meshDense)' : 'url(#ssc-meshGrid)'}
                opacity={meshStyle.opacity + 0.2}
              />
              <rect 
                x={frameThickness} 
                y={frameThickness} 
                width={svgWidth - (frameThickness * 2)} 
                height={svgHeight - (frameThickness * 2)} 
                fill="black"
                opacity={meshStyle.opacity}
              />

              {/* Handle */}
              {handleStyle === 'surface' ? (
                /* Classic: Surface mount handle overlapping frame about halfway */
                <g transform={`translate(${svgWidth - frameThickness - 8}, ${svgHeight / 2 - 8})`}>
                  <rect 
                    x="0" 
                    y="0" 
                    width="10" 
                    height="16" 
                    rx="1"
                    fill="#444" 
                    stroke="#222"
                    strokeWidth="1"
                  />
                </g>
              ) : (
                /* Premium: Flush handle recessed INTO the frame - position based on handleLocation */
                handleLocation !== 'none' && (
                  <g transform={`translate(${
                    handleLocation === 'left' 
                      ? (frameThickness / 2) - 4 
                      : svgWidth - frameThickness + (frameThickness / 2) - 4
                  }, ${svgHeight / 2 - 20})`}>
                    <rect 
                      x="0" 
                      y="0" 
                      width="8" 
                      height="40" 
                      rx="4"
                      fill="#222" 
                      opacity="0.9"
                    />
                  </g>
                )
              )}
            </g>
          </svg>
        </div>
        <div className="ssc-preview__caption">
           <p>Visual Representation</p>
        </div>

        {/* Reference image thumbnails — frame profile + measurement guide */}
        <div className="ssc-ref-gallery">
          <p className="ssc-ref-gallery__hint">
            Click to view real {frameType === 'premium' ? 'Premium' : 'Classic'} frame specs
          </p>
          <div className="ssc-ref-gallery__thumbs">
            {(REFERENCE_IMAGES[frameType] || REFERENCE_IMAGES.classic).map((img, i) => (
              <button
                key={img.url}
                type="button"
                className="ssc-ref-thumb"
                onClick={() => setLightboxImg(img)}
                title={img.label}
                aria-label={`View ${img.label}`}
              >
                <img src={img.url} alt={img.label} />
              </button>
            ))}
            <button
              type="button"
              className="ssc-ref-thumb"
              onClick={() => setLightboxImg(MEASUREMENT_GUIDE_IMG)}
              title={MEASUREMENT_GUIDE_IMG.label}
              aria-label={`View ${MEASUREMENT_GUIDE_IMG.label}`}
            >
              <img src={MEASUREMENT_GUIDE_IMG.url} alt={MEASUREMENT_GUIDE_IMG.label} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- Render ---
  return (
    <div className="ssc-configurator">
      
      {/* Price Header */}
      <div className="ssc-price-header">
        <span className="ssc-price-header__label">Your Price:</span>
        <span className="ssc-price-header__amount">${totalPrice.toFixed(2)}</span>
        <button
          type="button"
          className="ssc-save-build-btn"
          onClick={handleSaveMyBuild}
          disabled={saveStatus === 'saving'}
          aria-label="Save my build to come back to later"
        >
          <BookmarkIcon />
          <span>
            {saveStatus === 'saving' ? 'Saving…' :
             saveStatus === 'saved'  ? 'Saved!' :
             saveStatus === 'error'  ? 'Try again' :
                                       'Save my Build'}
          </span>
        </button>
      </div>

      {/* Restored / editing notice */}
      {restoredNotice && (
        <div className="ssc-restored-notice" role="status">
          <span>
            <BookmarkIcon />
            {editLineKey
              ? 'Editing your existing cart item. Add to Cart will replace it with the updated build.'
              : 'We restored your saved build. Pick up right where you left off.'}
          </span>
          <button type="button" className="ssc-restored-notice__dismiss" onClick={handleClearSavedBuild}>
            Start over
          </button>
        </div>
      )}

      {/* Two Column Layout on Desktop */}
      <div className="ssc-layout">
        
        {/* Preview Column (sticky on desktop) */}
        {showPreview && (
          <div className="ssc-layout__preview" ref={previewColumnRef}>
            <div className="ssc-preview-card" ref={previewInnerRef}>
              <div className="ssc-preview-card__header">
                <h3>Live Preview</h3>
                <span className="ssc-preview-card__badge">
                  {frameType === 'classic' ? 'Classic Frame' : 'Premium Frame'}
                </span>
              </div>
              <DoorPreview />
            </div>

            {showPriceSummary && (
              <div className="ssc-summary">
                <h3 className="ssc-summary__title">Configuration Summary</h3>
                <div className="ssc-summary__items">
                  <div className="ssc-summary__item">
                    <span>Dimensions</span>
                    <span>{totalWidth}" x {totalHeight}"</span>
                  </div>
                  <div className="ssc-summary__item">
                    <span>Frame</span>
                    <span>{COLORS[color].name} {frameType === 'classic' ? 'Classic' : 'Premium'}</span>
                  </div>
                  <div className="ssc-summary__item">
                    <span>Mesh</span>
                    <span>{MESH_STYLES[mesh].name}</span>
                  </div>
                  <div className="ssc-summary__item">
                    <span>Assembly</span>
                    <span>{assembly === 'assembled' ? 'Pre-Assembled' : 'DIY Kit'}</span>
                  </div>
                </div>
                <div className="ssc-summary__total">
                  <span>Estimated Total</span>
                  <span className="ssc-summary__price">${totalPrice.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Configuration Form Column */}
        <div className="ssc-layout__form">
          
          {/* Assembly Selection */}
          <section className="ssc-section">
            <h2 className="ssc-section__title">
              <span className="ssc-section__number">1</span>
              Assembly Options
            </h2>
            <div className={assembledAvailable ? 'ssc-grid ssc-grid--2' : 'ssc-grid'}>
              <RadioCard
                selected={assembly === 'unassembled'}
                onClick={() => setAssembly('unassembled')}
                title="Unassembled (DIY)"
                subtitle="Ships compact. Requires assembly."
                price={PRICES.assembly.unassembled}
              >
                <div className="ssc-radio-card__extra">
                   <div className="ssc-diy-badge">
                     <PlayIcon />
                     <span>Easy DIY Assembly</span>
                   </div>
                   <p>Save big on shipping & assembly costs. Detailed instructions included.</p>
                   <a
                     href="https://www.youtube.com/watch?v=fjzhcCj0Db0"
                     target="_blank"
                     rel="noreferrer"
                     onClick={(e) => e.stopPropagation()}
                     className="ssc-link"
                   >
                     View Assembly Video Guide
                   </a>
                </div>
              </RadioCard>

              {assembledAvailable && (
              <RadioCard
                selected={assembly === 'assembled'}
                onClick={() => setAssembly('assembled')}
                title="Assembled"
                subtitle="Ships ready to install."
                price={PRICES.assembly.assembled}
              >
                 <div className="ssc-radio-card__extra">
                    <div className="ssc-warning">
                       <AlertIcon />
                       <span><strong>Shipping Notice:</strong> Due to package size, shipping costs for assembled doors are significantly higher than DIY kits.</span>
                    </div>
                 </div>
              </RadioCard>
              )}
            </div>
            {!assembledAvailable && (
              <p className="ssc-color-notice">
                <AlertIcon />
                Assembled doors are temporarily unavailable. All orders ship as DIY kits with simple instructions and free assembly support.
              </p>
            )}
          </section>

          {/* Frame Type */}
          <section className="ssc-section">
            <h2 className="ssc-section__title">
              <span className="ssc-section__number">2</span>
              Frame Style
            </h2>
            <div className="ssc-grid ssc-grid--2">
              <RadioCard 
                selected={frameType === 'classic'}
                onClick={() => setFrameType('classic')}
                title="Classic Frame"
                subtitle="1-5/8 x 1/2 in. Surface handle."
                price={0}
              />
              <RadioCard 
                selected={frameType === 'premium'}
                onClick={() => setFrameType('premium')}
                title="Premium Frame"
                subtitle="2 x 1 in. Heavy duty. Flush handle."
                price={PRICES.frameType.premium}
              />
            </div>
            
            {/* Handle Location - Premium Only */}
            {frameType === 'premium' && (
              <div className="ssc-handle-location">
                <label className="ssc-handle-location__label">Handle Location (From Outside Looking In)</label>
                <div className="ssc-handle-location__options">
                  <button
                    type="button"
                    className={`ssc-handle-btn ${handleLocation === 'left' ? 'ssc-handle-btn--selected' : ''}`}
                    onClick={() => setHandleLocation('left')}
                  >
                    Left
                  </button>
                  <button
                    type="button"
                    className={`ssc-handle-btn ${handleLocation === 'right' ? 'ssc-handle-btn--selected' : ''}`}
                    onClick={() => setHandleLocation('right')}
                  >
                    Right
                  </button>
                  <button
                    type="button"
                    className={`ssc-handle-btn ${handleLocation === 'none' ? 'ssc-handle-btn--selected' : ''}`}
                    onClick={() => setHandleLocation('none')}
                  >
                    No Handle
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Dimensions */}
          <section className="ssc-section">
            <h2 className="ssc-section__title">
              <span className="ssc-section__number">3</span>
              Dimensions
            </h2>
            <div className="ssc-info-box">
              <InfoIcon />
              <p>
                Measure your door opening carefully. We deduct standard tolerances for fit. 
                Enter the <strong>exact size</strong> you need the door to be (frame end to frame end).
                Each door is built to a <strong>±1/8" manufacturing tolerance</strong>.
              </p>
            </div>

            <div className="ssc-grid ssc-grid--2">
              {/* Width */}
              <div className="ssc-field">
                <label className="ssc-field__label">Width (Inches)</label>
                <div className="ssc-field__row">
                  <select 
                    value={width} 
                    onChange={(e) => setWidth(Number(e.target.value))}
                    className="ssc-select"
                  >
                    {Array.from({ length: WIDTH_RANGE.max - WIDTH_RANGE.min + 1 }, (_, i) => WIDTH_RANGE.min + i).map(num => (
                      <option key={num} value={num}>{num}"</option>
                    ))}
                  </select>
                  <select 
                    value={widthFraction} 
                    onChange={(e) => setWidthFraction(Number(e.target.value))}
                    className="ssc-select ssc-select--fraction"
                  >
                    {FRACTIONS.map(f => (
                      <option key={f.val} value={f.val}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Height */}
              <div className="ssc-field">
                <label className="ssc-field__label">Height (Inches)</label>
                <div className="ssc-field__row">
                  <select 
                    value={height} 
                    onChange={(e) => setHeight(Number(e.target.value))}
                    className="ssc-select"
                  >
                    {Array.from({ length: HEIGHT_RANGE.max - HEIGHT_RANGE.min + 1 }, (_, i) => HEIGHT_RANGE.min + i).map(num => (
                      <option key={num} value={num}>{num}"</option>
                    ))}
                  </select>
                  <select 
                    value={heightFraction} 
                    onChange={(e) => setHeightFraction(Number(e.target.value))}
                    className="ssc-select ssc-select--fraction"
                  >
                    {FRACTIONS.map(f => (
                      <option key={f.val} value={f.val}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Frame Color */}
          <section className="ssc-section">
             <h2 className="ssc-section__title">
              <span className="ssc-section__number">4</span>
              Frame Color
            </h2>
            <div className="ssc-colors">
              {availableColors.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`ssc-color-btn ${color === c ? 'ssc-color-btn--selected' : ''}`}
                  type="button"
                >
                  <div 
                    className="ssc-color-btn__swatch" 
                    style={{ backgroundColor: COLORS[c].hex }}
                  />
                  <span className={`ssc-color-btn__label ${color === c ? 'ssc-color-btn__label--selected' : ''}`}>
                    {COLORS[c].name}
                  </span>
                </button>
              ))}
            </div>
            {frameType === 'premium' && (
               <p className="ssc-color-notice">
                 <AlertIcon />
                 Mill, Black, and White finishes are not available in Premium frame style.
               </p>
            )}
          </section>

          {/* Mesh Selection */}
          <section className="ssc-section">
             <h2 className="ssc-section__title">
              <span className="ssc-section__number">5</span>
              Mesh Type
            </h2>
            <div className="ssc-mesh-options">
              {Object.keys(MESH_STYLES).map(mKey => (
                 <div 
                   key={mKey}
                   onClick={() => setMesh(mKey)}
                   className={`ssc-mesh-option ${mesh === mKey ? 'ssc-mesh-option--selected' : ''}`}
                 >
                   <div className="ssc-mesh-option__content">
                     <div className="ssc-mesh-option__header">
                       <span className={`ssc-mesh-option__name ${mesh === mKey ? 'ssc-mesh-option__name--selected' : ''}`}>
                         {MESH_STYLES[mKey].name}
                       </span>
                       <span className={`ssc-mesh-option__price ${PRICES.mesh[mKey] > 0 ? '' : 'ssc-mesh-option__price--included'}`}>
                         {PRICES.mesh[mKey] === 0 ? 'Included' : `+$${PRICES.mesh[mKey].toFixed(2)}`}
                       </span>
                     </div>
                     <p className="ssc-mesh-option__desc">
                       {mKey === 'standard' && 'Standard visibility and airflow.'}
                       {mKey === 'solar' && 'Reduces heat and glare. Darker appearance.'}
                       {mKey === 'bettervue' && 'Improved visibility and airflow.'}
                       {mKey === 'ultravue' && 'Premium invisibility and airflow.'}
                       {mKey === 'petscreen' && 'Heavy duty, tear resistant. Thicker mesh.'}
                     </p>
                   </div>
                   <div className={`ssc-mesh-option__check ${mesh === mKey ? 'ssc-mesh-option__check--selected' : ''}`}>
                     {mesh === mKey && <CheckIcon />}
                   </div>
                 </div>
              ))}
            </div>
          </section>

          {/* Add-to-Cart card — lives in the right column under the form, so it
              sits beside the (sticky) preview on desktop and slots in at the
              end of the flow on mobile. No volume discount messaging — doors
              are excluded from the bulk-pricing program. */}
          <div className="ssc-atc-card" ref={atcCardRef}>
            <div className="ssc-atc-card__chips">
              <span className="ssc-atc-card__chip">
                {totalWidth}" × {totalHeight}"
              </span>
              <span className="ssc-atc-card__chip">
                {COLORS[color].name} {frameType === 'premium' ? 'Premium' : 'Classic'}
              </span>
              <span className="ssc-atc-card__chip">
                {MESH_STYLES[mesh].name}
              </span>
              {assembledAvailable && (
                <span className="ssc-atc-card__chip">
                  {assembly === 'assembled' ? 'Pre-Assembled' : 'DIY Kit'}
                </span>
              )}
              {frameType === 'premium' && handleLocation !== 'none' && (
                <span className="ssc-atc-card__chip">
                  {handleLocation === 'left' ? 'Left' : 'Right'} handle
                </span>
              )}
            </div>

            <div className="ssc-atc-card__row">
              <div className="ssc-atc-card__total">
                <div className="ssc-atc-card__total-label">Estimated Total</div>
                <div className="ssc-atc-card__total-amount">
                  ${cartSubtotal.toFixed(2)}
                </div>
                {quantity > 1 && (
                  <div className="ssc-atc-card__total-detail">
                    {quantity} × ${totalPrice.toFixed(2)}
                  </div>
                )}
              </div>

              {/* Quantity stepper */}
              <div className="ssc-atc-card__qty" role="group" aria-label="Quantity">
                <button
                  type="button"
                  className="ssc-atc-card__qty-btn"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  <MinusIcon />
                </button>
                <input
                  type="number"
                  className="ssc-atc-card__qty-input"
                  value={quantity}
                  min={1}
                  max={MAX_QTY}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setQuantity(Math.max(1, Math.min(MAX_QTY, v)));
                  }}
                  aria-label="Quantity"
                />
                <button
                  type="button"
                  className="ssc-atc-card__qty-btn"
                  onClick={() => setQuantity(q => Math.min(MAX_QTY, q + 1))}
                  disabled={quantity >= MAX_QTY}
                  aria-label="Increase quantity"
                >
                  <PlusIcon />
                </button>
              </div>

              <button
                type="button"
                className="ssc-atc-card__cta"
                onClick={handleAddToCart}
                disabled={cartStatus === 'adding'}
              >
                <CartIcon />
                <span>
                  {cartStatus === 'adding' ? 'Adding…' :
                   cartStatus === 'added'  ? 'Added to Cart' :
                                             'Add to Cart'}
                </span>
              </button>
            </div>

            <p className="ssc-atc-card__footer">
              Custom built in the USA · Ships within 5 business days
            </p>
          </div>

        </div>
      </div>

      {/* Lightbox for reference images */}
      {lightboxImg && (
        <div
          className="ssc-lightbox"
          role="dialog"
          aria-label="Reference image"
          onClick={() => setLightboxImg(null)}
        >
          <div className="ssc-lightbox__inner" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="ssc-lightbox__close"
              onClick={() => setLightboxImg(null)}
              aria-label="Close"
            >
              ×
            </button>
            <img src={lightboxImg.url} alt={lightboxImg.label} />
            {lightboxImg.label && (
              <div className="ssc-lightbox__caption">{lightboxImg.label}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
